import React, { useState, useEffect } from 'react';
import { adApi } from '../../utils/api';
import AD_SIZES from './adssize';

function AdBanner({ 
  label = 'विज्ञापन', 
  position, 
  image, 
  link, 
  className = '', 
  noFallback = false,
  maxAds = 2,  // Maximum number of ads to show in same position (vertical stack)
  showAll = false,  // If true, show all ads for this position
  onLoad
}) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Size for this placement comes from adssize.jsx (edit numbers there)
  const size = AD_SIZES[position] || { width: 728, height: 120 };

  useEffect(() => {
    if (!position) {
      setLoading(false);
      return;
    }
    let mounted = true;
    adApi
      .getByPosition(position)
      .then((res) => {
        if (mounted) {
          const adList = res.data || [];
          // Sort by created_at descending (newest first)
          const sortedAds = adList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          // Limit the number of ads shown
          const limitedAds = showAll ? sortedAds : sortedAds.slice(0, maxAds);
          setAds(limitedAds);
          onLoad?.(limitedAds.length > 0);
        }
      })
      .catch(() => {
        if (mounted) {
          setAds([]);
          onLoad?.(false);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [position, maxAds, showAll, onLoad]);

  // Count a real click on the admin-managed ad (fire and forget)
  const handleClick = (adId) => {
    if (adId) {
      adApi.recordClick(adId).catch(() => {});
    }
  };

  if (loading) return null;
  if (!image && (!ads || ads.length === 0)) {
    if (noFallback) return null;
    // For single image/link props (backward compatibility)
    if (image || link) return null;
    return null;
  }

  // If image/link props provided directly, render single ad (backward compatibility)
  if (image || link) {
    const src = image;
    const href = link || '#';
    return (
      <a
        href={href}
        target={href && href !== '#' ? '_blank' : undefined}
        rel="noreferrer"
        onClick={handleClick}
        aria-label={label}
        className={`block rounded-xl overflow-hidden border border-line/70 bg-paper-soft mx-auto transition-transform hover:scale-[1.01] ${className}`}
        style={{ width: '100%', maxWidth: size.width }}
      >
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="w-full h-auto object-contain"
        />
      </a>
    );
  }

  // Render multiple ads vertically stacked
  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {ads.map((ad, index) => (
        <a
          key={ad.id || index}
          href={ad.link || '#'}
          target={ad.link ? '_blank' : undefined}
          rel="noreferrer"
          onClick={() => handleClick(ad.id)}
          aria-label={`${label} ${index + 1}`}
          className="block rounded-xl overflow-hidden border border-line/70 bg-paper-soft mx-auto transition-transform hover:scale-[1.01]"
          style={{ width: '100%', maxWidth: size.width }}
        >
          <img
            src={ad.image}
            alt={`${label} ${index + 1}`}
            loading="lazy"
            className="w-full h-auto object-contain"
          />
        </a>
      ))}
    </div>
  );
}

export default AdBanner;
